import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  clearReadNotifications
} from '../../store/slices/notificationsSlice';
import { useThemeColors } from '../../hooks/useThemeColors';

const formatRelative = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  const hr = Math.floor(min / 60);
  const days = Math.floor(hr / 24);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TYPE_META = {
  lead_created: {
    color: '#10B981',
    bg: '#ECFDF5',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0" />
        <circle cx="17" cy="9" r="2.5" /><path d="M15 20a4.5 4.5 0 016.5-4" />
      </svg>
    )
  },
  lead_assigned: {
    color: '#0EA5E9',
    bg: '#ECFEFF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0116 0" />
      </svg>
    )
  },
  lead_status_changed: {
    color: '#6366F1',
    bg: '#EEF2FF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 0115-6.7L21 8M21 12a9 9 0 01-15 6.7L3 16" /><path d="M21 3v5h-5M3 21v-5h5" />
      </svg>
    )
  },
  lead_critical: {
    color: '#EF4444',
    bg: '#FEF2F2',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 22h20L12 2z" /><path d="M12 9v6M12 19v.01" />
      </svg>
    )
  },
  proposal_created: {
    color: '#8B5CF6',
    bg: '#F5F3FF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" />
      </svg>
    )
  },
  proposal_sent: {
    color: '#3B82F6',
    bg: '#EFF6FF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
      </svg>
    )
  },
  proposal_approved: {
    color: '#10B981',
    bg: '#ECFDF5',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" />
      </svg>
    )
  },
  proposal_rejected: {
    color: '#EF4444',
    bg: '#FEF2F2',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" />
      </svg>
    )
  },
  project_created: {
    color: '#0EA5E9',
    bg: '#ECFEFF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V4h8v3" />
      </svg>
    )
  },
  project_status_changed: {
    color: '#F59E0B',
    bg: '#FFFBEB',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 0115-6.7L21 8M21 12a9 9 0 01-15 6.7L3 16" />
      </svg>
    )
  },
  project_completed: {
    color: '#10B981',
    bg: '#ECFDF5',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" />
      </svg>
    )
  },
  change_order_created: {
    color: '#8B5CF6',
    bg: '#F5F3FF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4v16M4 12h16" />
      </svg>
    )
  },
  change_order_approved: {
    color: '#10B981',
    bg: '#ECFDF5',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" />
      </svg>
    )
  },
  crew_assigned: {
    color: '#0891B2',
    bg: '#ECFEFF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    )
  },
  schedule_updated: {
    color: '#F59E0B',
    bg: '#FFFBEB',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </svg>
    )
  },
  schedule_digest: {
    color: '#6366F1',
    bg: '#EEF2FF',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" />
      </svg>
    )
  },
  system: {
    color: '#6B7280',
    bg: '#F3F4F6',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
      </svg>
    )
  }
};

const filtersConfig = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'lead', label: 'Leads' },
  { id: 'proposal', label: 'Proposals' },
  { id: 'project', label: 'Projects' },
  { id: 'schedule', label: 'Schedule' }
];

const NotificationsDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { buttonColors } = useThemeColors();
  const accentColor = buttonColors?.primary || '#6366F1';
  const { items, unreadCount, loading } = useSelector((s) => s.notifications);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (open) dispatch(fetchNotifications({ limit: 50 }));
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'unread') return items.filter((n) => !n.read);
    if (filter === 'schedule') return items.filter((n) => n.entityType === 'event' || n.entityType === 'schedule');
    return items.filter((n) => n.entityType === filter);
  }, [items, filter]);

  if (!open) return null;

  const handleClick = (n) => {
    if (!n.read) dispatch(markNotificationRead(n._id));
    // Note: clicking does not navigate. The notification stays in the dialog
    // for the user to read. Deep links can be added later if needed.
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-[80vh] max-h-[760px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${accentColor}1A`, color: accentColor }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16l-2-3z" />
                <path d="M10 19a2 2 0 004 0" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 leading-tight">Notifications</p>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-500 shrink-0"
            aria-label="Close"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 shrink-0 overflow-x-auto hide-scrollbar">
          {filtersConfig.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-all border ${
                  active ? 'text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                style={active ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
              >
                {f.label}
                {f.id === 'unread' && unreadCount > 0 && (
                  <span
                    className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                      active ? 'bg-white' : 'text-white'
                    }`}
                    style={active ? { color: accentColor } : { backgroundColor: accentColor }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
          <div className="flex-1" />
          {unreadCount > 0 && (
            <button
              onClick={() => dispatch(markAllNotificationsRead())}
              className="text-[11.5px] font-semibold text-gray-600 hover:text-gray-900 whitespace-nowrap"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {loading && items.length === 0 ? (
            <div className="px-6 py-16 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 mx-auto flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16l-2-3z" />
                  <path d="M10 19a2 2 0 004 0" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900">No notifications</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {filter === 'unread' ? 'Nothing unread right now.' : 'Activity will appear here as it happens.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {filtered.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.system;
                const actor = n.actor;
                const actorName = actor
                  ? `${actor.profile?.firstName || ''} ${actor.profile?.lastName || ''}`.trim() || actor.email
                  : '';
                return (
                  <li key={n._id}>
                    <div
                      onClick={() => handleClick(n)}
                      className={`px-4 py-3 flex items-start gap-3 group cursor-pointer transition-colors ${
                        n.read ? 'hover:bg-gray-50/70' : 'bg-blue-50/30 hover:bg-blue-50/60'
                      }`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: meta.bg, color: meta.color }}
                      >
                        {meta.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <p className={`text-[13px] leading-tight ${n.read ? 'text-gray-800 font-medium' : 'text-gray-900 font-semibold'}`}>
                            {n.title}
                          </p>
                          {!n.read && (
                            <span
                              className="w-2 h-2 rounded-full shrink-0 mt-1.5"
                              style={{ backgroundColor: accentColor }}
                            />
                          )}
                        </div>
                        {n.message && (
                          <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">{n.message}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-400">
                          <span>{formatRelative(n.createdAt)}</span>
                          {actorName && (
                            <>
                              <span>·</span>
                              <span className="truncate">{actorName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(deleteNotification(n._id));
                        }}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-200 text-gray-400 hover:text-gray-700 shrink-0 transition-all"
                        aria-label="Dismiss"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <p className="text-[11.5px] text-gray-500">
            {filtered.length} of {items.length} shown
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => dispatch(clearReadNotifications())}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
            >
              Clear read
            </button>
            <button
              onClick={onClose}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsDialog;
